/**
 * AlertsOverviewPage — live cross-tenant alert overview (Session 2026-04-18
 * Q4: refresh every 10 seconds). Renders one AlertRegionCard per region
 * currently under alert; shows an empty state when nothing is active.
 *
 * 014-alerts / US7 / T134.
 */
import { Box, Grid, Stack, Text, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAlertsOverview, useEndSimulation } from '../hooks/useAdminAlerts';
import { AlertRegionCard } from '../components/AlertRegionCard';

export function AlertsOverviewPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, isLoading, isError } = useAlertsOverview();
  const endSim = useEndSimulation();

  const regions = data?.regions ?? [];

  const handleEndSimulation = (alertEventId: string) => {
    endSim.mutate(alertEventId, {
      onSuccess: () => {
        toast({
          status: 'success',
          title: t('admin.alerts.overview.endSimSuccessTitle', 'Simulation stopped'),
          duration: 4000,
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: t('admin.alerts.overview.endSimFailedTitle', "Couldn't stop simulation"),
          description: t('admin.alerts.overview.endSimFailedDetail', 'Try again in a moment.'),
          duration: 6000,
        });
      },
    });
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="admin-alerts-overview">
      <Stack spacing={4}>
        <Text color="gray.600">
          {t(
            'admin.alerts.overview.description',
            'Live view of all currently-active air-raid alerts across every tenant. Refreshes every 10 seconds.',
          )}
        </Text>

      {isError && (
        <Box p={4} bg="red.50" color="red.700" borderRadius="md">
          {t('admin.alerts.overview.loadError', 'Failed to load the overview. It will retry automatically.')}
        </Box>
      )}

      {!isLoading && regions.length === 0 && !isError && (
        <Box p={8} bg="green.50" borderRadius="md" textAlign="center">
          <Text fontSize="lg" color="green.700" fontWeight="semibold">
            {t('admin.alerts.overview.empty', 'No active alerts.')}
          </Text>
          <Text fontSize="sm" color="green.600" mt={1}>
            {t('admin.alerts.overview.emptyDetail', 'All tenants are clear right now.')}
          </Text>
        </Box>
      )}

        <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={4}>
          {regions.map((group) => (
            <AlertRegionCard
              key={group.regionId}
              group={group}
              onEndSimulation={handleEndSimulation}
              isEndingSimulation={endSim.isPending}
            />
          ))}
        </Grid>
      </Stack>
    </Box>
  );
}
